import React, { FC, Dispatch, SetStateAction, useEffect, useState } from "react";
import {
  View,
  StyleSheet,
  Platform,
  Animated, Easing
} from "react-native";
import { useNavigation } from "@react-navigation/native";

import {
  CodeField,
  Cursor,
  useBlurOnFulfill,
  useClearByFocusCell,
} from 'react-native-confirmation-code-field';
import * as Haptics from 'expo-haptics';

import CustomComponent from "../../../components/CustomComponent";
import GlobalStyles from "../../../globalStyles/globalStyles";
import { Colors } from "../../../globalStyles/ColorConfig";

type OTPInputMobileProps = {
  setCode: Dispatch<SetStateAction<string>>;
  maximumLength: number;
  onOtpCompleted?: () => void;
  error: boolean;
  errorTitle: string;
  handleError: any;
};

const OTPInputMobile: FC<OTPInputMobileProps> = ({
  setCode,
  maximumLength,
  onOtpCompleted,
  error = false,
  errorTitle,
  handleError
}) => {
  const navigation = useNavigation();
  const CELL_COUNT = 6;

  const [shakeAnimation] = useState(new Animated.Value(0));
  const [value, setValue] = useState('');
  const ref = useBlurOnFulfill({value, cellCount: CELL_COUNT});
  const [props, getCellOnLayoutHandler] = useClearByFocusCell({
    value,
    setValue
  });

  useEffect(() => {
    if (value.length === maximumLength && onOtpCompleted) {
      onOtpCompleted();
    }
  }, [value]);

  useEffect(() => {
    if (error) {
      Haptics.selectionAsync()
      setValue(''); 
      triggerShakeAnimation()
    }
  }, [error]);

  const onChange = (text:any) => {
    handleError(false)
    setCode(text);
    setValue(text)
    console.log(text)
  }

  const triggerShakeAnimation = () => {
    Animated.sequence([
      Animated.timing(shakeAnimation, { toValue: 10, duration: 50, easing: Easing.linear, useNativeDriver: true }),
      Animated.timing(shakeAnimation, { toValue: -10, duration: 50, easing: Easing.linear, useNativeDriver: true }),
      Animated.timing(shakeAnimation, { toValue: 10, duration: 50, easing: Easing.linear, useNativeDriver: true }),
      Animated.timing(shakeAnimation, { toValue: 0, duration: 50, easing: Easing.linear, useNativeDriver: true })
    ]).start();
  };

  const interpolatedShake = shakeAnimation.interpolate({
    inputRange: [-1, 1],
    outputRange: ['-1deg', '1deg']
  });

  const animatedStyle = {
    transform: [{ rotate: interpolatedShake }]
  };

  return (
    <View style={styles.container}>
      <View
        style={{
          flexDirection: "row",
          justifyContent: "space-between",
          alignItems: "center",
        }}
      >
        <CodeField
          ref={ref}
          {...props}
          value={value}
          onChangeText={(text)=>onChange(text)}
          cellCount={CELL_COUNT}
          textContentType="oneTimeCode"
          inputMode="decimal"
          renderCell={({index, symbol, isFocused}) => (
            <Animated.Text
              key={index}
              style={[styles.cell, isFocused && styles.focusCell, error && styles.errorCell, animatedStyle]}
              onLayout={getCellOnLayoutHandler(index)}>
              {symbol || (isFocused ? <Cursor/> : null)}
            </Animated.Text>
          )}
        />
      </View>
      {error && (
        <CustomComponent.TextView
          title={errorTitle}
          textStyle={[GlobalStyles.bodyText, { color: Colors.red10 }]}
          boxStyle={{ paddingVertical: 10 }}
        />
      )}
    </View>
  );
};

export default OTPInputMobile;

const styles = StyleSheet.create({
  root: {flex: 1, padding: 20},
  title: {textAlign: 'center', fontSize: 30},
  cell: {
    width: Platform.OS === 'web' ? 45 : '14.5%',
    height: 50,
    lineHeight: 38,
    fontSize: 24,
    borderWidth: 2,
    borderColor: '#00000030',
    textAlign: 'center',
    paddingTop: Platform.OS==="android" ? 5 : 2,
    borderRadius:10,
    marginHorizontal:3
  },
  focusCell: {
    borderColor: '#000',
  },
  container: {
    flexDirection: "column",
  },
  input: {
    width: 40,
    height: 40,
    textAlign: "center",
    borderWidth: 1,
    borderColor: "#00000030",
    fontSize: 20,
  },
  focusedInput: {
    borderColor: "#000",
  },
  errorCell: {
    borderColor: Colors.red10, // Change this to the color you want for error state
    borderWidth: 1
  },
});
